import { Fraction } from 'bi-fraction';
import { AbiCoder } from 'ethers';
import { BondingCurve as BondingCurveAbi, BondingCurveRouter as BondingCurveRouterAbi, BondingPair as BondingPairAbi, ERC20 as ERC20Abi, } from '../abis';
import { AGENT_TOKEN_DECIMALS, AGENTFUN_TAX, BONDING_TOKEN_DECIMALS, FUN_TOKEN_DECIMALS, L2_BASE_TOKEN_ADDRESS, ZKCRO_DECIMALS, } from '../constants';
import { getContract } from '../utils';
import { approveIfRequired } from '../utils/approveIfRequired';
import { executeTx } from '../utils/executeTx';
import { getAgentfunRemainingForGrad } from '../utils/getAgentfunRemainingForGrad';
import { getMcapAtGraduation } from '../utils/getMcapAtGraduation';
import { isSigner } from '../utils/isSigner';
export class BondingCurveModule {
    constructor(options) {
        this.options = options;
    }
    get bondingCurve() {
        return getContract(this.options.addresses.BondingCurve, BondingCurveAbi, this.options.signerOrProvider);
    }
    get bondingCurveRouter() {
        return getContract(this.options.addresses.BondingCurveRouter, BondingCurveRouterAbi, this.options.signerOrProvider);
    }
    async buyTokens(amount, tokenAddress) {
        if (!isSigner(this.options.signerOrProvider))
            throw new Error('need signer for buyTokens function');
        const amountInBn = amount.shl(FUN_TOKEN_DECIMALS).quotient;
        await approveIfRequired(this.options.addresses.AgentFun, this.options.addresses.BondingCurveRouter, amountInBn, this.options.signerOrProvider);
        const receipt = await executeTx(this.bondingCurve.buy(amountInBn, tokenAddress));
        const logs = receipt?.logs;
        const returnedAmount = this.getReturnedAmountFromLogs(logs, await this.options.signerOrProvider.getAddress());
        return { receipt, returnedAmount };
    }
    async sellTokens(amount, tokenAddress) {
        if (!isSigner(this.options.signerOrProvider))
            throw new Error('need signer for sellTokens function');
        const amountInBn = amount.shl(AGENT_TOKEN_DECIMALS).quotient;
        await approveIfRequired(tokenAddress, this.options.addresses.BondingCurveRouter, amountInBn, this.options.signerOrProvider);
        const receipt = await executeTx(this.bondingCurve.sell(amountInBn, tokenAddress));
        const logs = receipt?.logs;
        const returnedAmount = this.getReturnedAmountFromLogs(logs, await this.options.signerOrProvider.getAddress());
        return { receipt, returnedAmount };
    }
    async estimateGasForTrade(amount, tokenAddress, type) {
        if (!isSigner(this.options.signerOrProvider))
            throw new Error('need signer for sellTokens function');
        const amountInBn = amount.shl(ZKCRO_DECIMALS).quotient;
        const latestBlockPromise = this.options.signerOrProvider.provider?.getBlock('latest');
        try {
            const estimateGas = type === 'sell'
                ? this.bondingCurve.sell.estimateGas
                : this.bondingCurve.buy.estimateGas;
            const [gasEstimate, latestBlock] = await Promise.all([
                estimateGas(amountInBn, tokenAddress),
                latestBlockPromise,
            ]);
            const baseFeePerGas = latestBlock?.baseFeePerGas ?? 0n;
            return {
                gasFee: new Fraction(gasEstimate * baseFeePerGas).shr(ZKCRO_DECIMALS),
                success: true,
            };
        }
        catch {
            return { gasFee: Fraction.ZERO, success: false };
        }
    }
    async getTokenInfo(tokenAddress) {
        const tokenInfo = await this.bondingCurve.tokenInfo(tokenAddress);
        return tokenInfo;
    }
    async getExpectedReceiveAmountForBuy(amount, tokenAddress) {
        return this.getExpectedReceiveAmount(amount, tokenAddress, 'buy');
    }
    async getExpectedReceiveAmountForSell(amount, tokenAddress) {
        return this.getExpectedReceiveAmount(amount, tokenAddress, 'sell');
    }
    async getTradingFee(fromAmount, bondingTokenAddress, type) {
        if (type === 'buy') {
            return fromAmount.mul(AGENTFUN_TAX);
        }
        else {
            const funAmountOut = await this.bondingCurveRouter
                .getAmountsOut(bondingTokenAddress, this.options.addresses.ZeroAddress, fromAmount.shl(FUN_TOKEN_DECIMALS).quotient)
                .then((v) => new Fraction(v).shr(FUN_TOKEN_DECIMALS));
            return funAmountOut.mul(AGENTFUN_TAX);
        }
    }
    async unwrapTokens(tokenAddress) {
        if (!isSigner(this.options.signerOrProvider))
            throw new Error('need signer for unwrapTokens function');
        const signerAddress = await this.options.signerOrProvider.getAddress();
        const receipt = await executeTx(this.bondingCurve.unwrapToken(tokenAddress, [signerAddress]));
        return receipt;
    }
    async getRemainingAgentfunAmount(bondingPairAddress) {
        const pair = getContract(bondingPairAddress, BondingPairAbi, this.options.signerOrProvider);
        const [[reserve0, reserve1], tokenA, gradThreshold, agentFunDeposited] = await Promise.all([
            pair.getReserves(),
            pair.tokenA(),
            this.bondingCurve.gradThreshold(),
            getContract(this.options.addresses.AgentFun, ERC20Abi, this.options.signerOrProvider).balanceOf(bondingPairAddress),
        ]);
        let agentfunReserves = reserve1;
        let bondingReserves = reserve0;
        if (
        // we read from contract if the first (A) token in the pair is agent fun or the bonding token
        tokenA.toLowerCase() === this.options.addresses.AgentFun.toLowerCase()) {
            agentfunReserves = reserve0;
            bondingReserves = reserve1;
        }
        const agentfunReservesFraction = new Fraction(agentfunReserves).shr(FUN_TOKEN_DECIMALS);
        const gradThresholdFraction = new Fraction(gradThreshold).shr(BONDING_TOKEN_DECIMALS);
        const bondingReservesFraction = new Fraction(bondingReserves).shr(BONDING_TOKEN_DECIMALS);
        let agentfunRemainingForGrad = getAgentfunRemainingForGrad({
            gradThreshold: gradThresholdFraction,
            agentfunCurrent: agentfunReservesFraction,
            bondingCurrent: bondingReservesFraction,
        });
        agentfunRemainingForGrad = agentfunRemainingForGrad.lt(Fraction.ZERO)
            ? Fraction.ZERO
            : agentfunRemainingForGrad;
        const mcapAtGraduation = getMcapAtGraduation({
            gradThreshold: gradThresholdFraction,
            k: agentfunReservesFraction.mul(bondingReservesFraction),
        });
        let bondingRemaining = bondingReservesFraction.sub(gradThresholdFraction);
        bondingRemaining = bondingRemaining.lt(Fraction.ZERO)
            ? Fraction.ZERO
            : bondingRemaining;
        return {
            agentfunRemaining: agentfunRemainingForGrad,
            agentfunDeposited: new Fraction(agentFunDeposited).shr(FUN_TOKEN_DECIMALS),
            bondingRemaining,
            mcapAtGraduation: mcapAtGraduation,
        };
    }
    async getExpectedAgentfunFee(amount, tokenAddress, type) {
        if (type === 'buy')
            return amount.mul(AGENTFUN_TAX);
        const assetTokenAddress = this.options.addresses.ZeroAddress;
        return new Fraction(await this.bondingCurveRouter.getAmountsOut(tokenAddress, assetTokenAddress, amount.shl(BONDING_TOKEN_DECIMALS).quotient))
            .shr(FUN_TOKEN_DECIMALS)
            .mul(AGENTFUN_TAX);
    }
    async getExpectedReceiveAmount(amount, tokenAddress, type) {
        let inTokenDecimals = FUN_TOKEN_DECIMALS;
        let outTokenDecimals = AGENT_TOKEN_DECIMALS;
        let assetTokenAddress = this.options.addresses.AgentFun;
        if (type === 'sell') {
            inTokenDecimals = AGENT_TOKEN_DECIMALS;
            assetTokenAddress = this.options.addresses.ZeroAddress;
            outTokenDecimals = FUN_TOKEN_DECIMALS;
        }
        const amountInBn = (type === 'buy' ? amount.mul(Fraction.ONE.sub(AGENTFUN_TAX)) : amount).shl(inTokenDecimals).quotient;
        const result = await this.bondingCurveRouter.getAmountsOut(tokenAddress, assetTokenAddress, amountInBn);
        return new Fraction(result)
            .mul(type === 'sell' ? Fraction.ONE.sub(AGENTFUN_TAX) : Fraction.ONE)
            .shr(outTokenDecimals);
    }
    // compare events here https://explorer.zkevm.cronos.org/testnet/tx/0x4c479237b05e603481ddfba6c683f4ccefb6ad1a6ea8e11dc1c3a8e772dac689#eventlog
    getReturnedAmountFromLogs(logs, receiverAddress) {
        if (!logs)
            return undefined;
        for (const log of logs) {
            if (log.topics[0].toLowerCase() ===
                BondingCurveModule.TRANSFER_EVENT_HASH &&
                new AbiCoder().decode(['address'], log.topics[1])[0].toLowerCase() !==
                    L2_BASE_TOKEN_ADDRESS &&
                new AbiCoder().decode(['address'], log.topics[2])[0].toLowerCase() ===
                    receiverAddress.toLowerCase()) {
                const amount = new AbiCoder().decode(['uint256'], log.data)[0];
                return new Fraction(amount).shr(
                // using to receive any of those tokens, will remind us to change code if decimals different
                AGENT_TOKEN_DECIMALS);
            }
        }
        return undefined;
    }
}
BondingCurveModule.TRANSFER_EVENT_HASH = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';
